import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

function isAuthKey(name) {
  return typeof name === 'string' && name.startsWith('sb-') && name.includes('-auth-token');
}

export function usesIosAuthStorage(platform = Capacitor) {
  return Boolean(
    platform
    && typeof platform.isNativePlatform === 'function'
    && platform.isNativePlatform()
    && typeof platform.getPlatform === 'function'
    && platform.getPlatform() === 'ios'
  );
}

/**
 * iOS WKWebView can evict localStorage. Preferences (UserDefaults) keeps the
 * agent session. Reads prefer Preferences and copy a WebView session across
 * on first launch so an update does not sign anyone out. Writes mirror both
 * so a Preferences failure still leaves a current token in the WebView.
 */
export function createSupabaseAuthStorage({ preferences, webStorage } = {}) {
  const native = preferences || Preferences;
  const web = webStorage !== undefined
    ? webStorage
    : (typeof localStorage !== 'undefined' ? localStorage : null);

  async function readWeb(key) {
    if (!web || typeof web.getItem !== 'function') return null;
    try {
      return web.getItem(key);
    } catch (error) {
      return null;
    }
  }

  return {
    async getItem(key) {
      try {
        const stored = await native.get({ key });
        if (stored && stored.value != null && stored.value !== '') return stored.value;
      } catch (error) {
        // Preferences failed. The WebView copy keeps this launch signed in.
      }
      const legacy = await readWeb(key);
      if (legacy != null && legacy !== '') {
        try {
          await native.set({ key, value: legacy });
        } catch (error) {
          // Returning the WebView value is what keeps the existing session.
        }
      }
      return legacy;
    },
    async setItem(key, value) {
      const text = String(value);
      try {
        await native.set({ key, value: text });
      } catch (error) {
        // The WebView mirror below still holds the rotated session.
      }
      if (web && typeof web.setItem === 'function') {
        try {
          web.setItem(key, text);
        } catch (error) {
          // Preferences already has the value when that write succeeded.
        }
      }
    },
    async removeItem(key) {
      try {
        await native.remove({ key });
      } catch (error) {
        // Still clear the WebView copy.
      }
      if (web && typeof web.removeItem === 'function') {
        try {
          web.removeItem(key);
        } catch (error) {
          // Preferences removal already ran.
        }
      }
    },
  };
}

export async function clearSupabaseAuthPreferences(preferences, webStorage) {
  if (webStorage && typeof webStorage.length === 'number' && typeof webStorage.key === 'function') {
    const names = [];
    for (let i = 0; i < webStorage.length; i += 1) {
      const name = webStorage.key(i);
      if (isAuthKey(name)) names.push(name);
    }
    names.forEach((name) => {
      if (typeof webStorage.removeItem === 'function') webStorage.removeItem(name);
    });
  }
  if (!preferences || typeof preferences.keys !== 'function') return [];
  try {
    const listed = await preferences.keys();
    const keys = (listed && listed.keys) || [];
    const removed = [];
    for (const key of keys) {
      if (!isAuthKey(key)) continue;
      await preferences.remove({ key });
      removed.push(key);
    }
    return removed;
  } catch (error) {
    return [];
  }
}

export async function clearNativeSupabaseAuth() {
  if (!usesIosAuthStorage()) return [];
  const web = typeof localStorage !== 'undefined' ? localStorage : null;
  return clearSupabaseAuthPreferences(Preferences, web);
}
